/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { preferencesService } from '../../services/api';
import toast from 'react-hot-toast';
import { Button, Skeleton } from '../../components/console/ui';
import { isVMManagerEnabled } from '../../config/featureFlags';
import type {
  NotificationChannelPrefs,
  NotificationEventKey,
  NotificationPreferences,
  NotificationPreferencesUpdate,
} from '../../types';

interface NotificationSetting {
  id: NotificationEventKey;
  label: string;
  description: string;
}

interface NotificationCategory {
  id: string;
  title: string;
  description: string;
  settings: NotificationSetting[];
}

const notificationCategories: NotificationCategory[] = [
  {
    id: 'resources',
    title: 'Resource Notifications',
    description: 'Get notified about your compute resources',
    settings: [
      {
        id: 'pod_status',
        label: 'Pod status changes',
        description: 'When pods start, stop, or encounter errors',
      },
      {
        id: 'vm_status',
        label: 'VM status changes',
        description: 'When VMs start, stop, or encounter errors',
      },
      {
        id: 'resource_limits',
        label: 'Resource limit warnings',
        description: 'When approaching storage or compute limits',
      },
    ],
  },
  {
    id: 'billing',
    title: 'Billing & Payments',
    description: 'Stay informed about your billing status',
    settings: [
      {
        id: 'low_balance',
        label: 'Wallet balance low',
        description: 'When your wallet drops below the threshold above',
      },
      {
        id: 'payment_success',
        label: 'Payment confirmations',
        description: 'When payments are processed successfully',
      },
      {
        id: 'invoice',
        label: 'New invoices',
        description: 'When new invoices are generated',
      },
    ],
  },
  {
    id: 'security',
    title: 'Security Alerts',
    description: 'Important security-related notifications',
    settings: [
      {
        id: 'new_login',
        label: 'New sign-in alerts',
        description: 'When your account is accessed from a new device',
      },
      {
        id: 'api_token',
        label: 'API key activity',
        description: 'When API keys are created or revoked',
      },
    ],
  },
  {
    id: 'updates',
    title: 'Product Updates',
    description: 'Stay up to date with Podstack news',
    settings: [
      {
        id: 'feature_announcements',
        label: 'Feature announcements',
        description: 'New features and improvements',
      },
      {
        id: 'maintenance',
        label: 'Maintenance notices',
        description: 'Scheduled maintenance and downtime',
      },
    ],
  },
];

const ALL_EVENTS: NotificationEventKey[] = [
  'pod_status',
  'vm_status',
  'resource_limits',
  'low_balance',
  'payment_success',
  'invoice',
  'new_login',
  'password_change',
  'api_token',
  'feature_announcements',
  'maintenance',
];

const defaultEmail: Record<NotificationEventKey, boolean> = {
  pod_status: true,
  vm_status: true,
  resource_limits: true,
  low_balance: true,
  payment_success: true,
  invoice: false,
  new_login: true,
  password_change: true,
  api_token: false,
  feature_announcements: false,
  maintenance: true,
};

type Channels = Record<NotificationEventKey, NotificationChannelPrefs>;

interface FormState {
  channels: Channels;
  /** Raw input text; '' = platform default. */
  threshold: string;
}

// Normalise the API payload. Older backends have no `channels`/threshold, so
// in-app mirrors the single legacy flag (which is what the backend does too).
const toForm = (p?: NotificationPreferences): FormState => {
  const channels = {} as Channels;
  ALL_EVENTS.forEach((k) => {
    const email = p?.[k] ?? defaultEmail[k];
    const ch = p?.channels?.[k];
    channels[k] = { email: ch?.email ?? email, in_app: ch?.in_app ?? email };
  });
  const t = p?.low_balance_threshold;
  return { channels, threshold: t === null || t === undefined ? '' : String(t) };
};

const parseThreshold = (raw: string): number | null | 'invalid' => {
  const cleaned = raw.replace(/[₹,\s]/g, '');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 && n <= 10_000_000 ? n : 'invalid';
};

const NotificationSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(() => toForm());
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch notification preferences - gracefully handle if API not available
  const { data: fetchedPreferences, isLoading } = useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: async (): Promise<NotificationPreferences | null> => {
      try {
        const response = await preferencesService.getNotifications();
        return response.data as NotificationPreferences;
      } catch {
        return null;
      }
    },
    retry: false,
  });

  // Per-channel + threshold are only shown when the backend reports them.
  const supportsChannels = !!fetchedPreferences?.channels;
  const supportsThreshold = !!fetchedPreferences && 'low_balance_threshold' in fetchedPreferences;

  useEffect(() => {
    setForm(toForm(fetchedPreferences ?? undefined));
    setHasChanges(false);
  }, [fetchedPreferences]);

  const updateMutation = useMutation({
    mutationFn: (data: NotificationPreferencesUpdate) => preferencesService.updateNotifications(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
      setHasChanges(false);
      toast.success('Notification preferences saved');
    },
    onError: () => {
      toast.error('Failed to save preferences');
    },
  });

  const toggle = (id: NotificationEventKey, channel: keyof NotificationChannelPrefs) => {
    setForm((prev) => {
      const cur = prev.channels[id];
      const next = { ...cur, [channel]: !cur[channel] };
      // Older backend: a single flag drives both, so keep them in lock-step.
      if (!supportsChannels) {
        next.email = next[channel];
        next.in_app = next[channel];
      }
      return { ...prev, channels: { ...prev.channels, [id]: next } };
    });
    setHasChanges(true);
  };

  const threshold = parseThreshold(form.threshold);

  const handleSave = () => {
    if (threshold === 'invalid') {
      toast.error('Enter a threshold between ₹0 and ₹1,00,00,000, or leave it empty');
      return;
    }
    // Flat flags = email (understood by every backend version).
    const payload: NotificationPreferencesUpdate = {};
    ALL_EVENTS.forEach((k) => {
      payload[k] = form.channels[k].email;
    });
    if (supportsChannels) {
      payload.channels = { ...form.channels };
    }
    if (supportsThreshold) {
      payload.low_balance_threshold = threshold;
    }
    updateMutation.mutate(payload);
  };

  if (isLoading) {
    return <Skeleton className="h-[420px] rounded-[14px]" />;
  }

  const rowCls = supportsChannels
    ? 'grid grid-cols-[minmax(0,1fr)_56px_56px] sm:grid-cols-[minmax(0,1fr)_72px_72px] gap-3 items-center py-3.5 border-t border-ps-raised'
    : 'flex justify-between items-center gap-3 py-3.5 border-t border-ps-raised cursor-pointer';
  const checkboxCls = 'w-[18px] h-[18px] accent-[#F97316] cursor-pointer flex-none justify-self-center';
  const hdCls = 'text-ps-subtle text-xs uppercase tracking-[0.04em] text-center';
  const events = notificationCategories
    .flatMap((c) => c.settings)
    .filter((s) => s.id !== 'vm_status' || isVMManagerEnabled());

  return (
    <>
      {supportsThreshold && (
        <section className="ps-card px-5 sm:px-6 py-5 flex flex-col gap-3" aria-labelledby="bal-heading">
          <h2 id="bal-heading" className="m-0 text-lg font-medium text-ps-text">
            Low-balance alert
          </h2>
          <div className="flex gap-2.5 items-center flex-wrap text-sm">
            <span className="text-ps-muted">Warn me when the wallet drops below</span>
            <label className="sr-only" htmlFor="low-balance-threshold">
              Low-balance threshold in rupees
            </label>
            <div className="relative w-[160px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ps-subtle pointer-events-none" aria-hidden="true">
                ₹
              </span>
              <input
                id="low-balance-threshold"
                className="ps-input w-full pl-7"
                inputMode="decimal"
                placeholder="Default"
                value={form.threshold}
                aria-invalid={threshold === 'invalid'}
                aria-describedby="low-balance-help"
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, threshold: e.target.value }));
                  setHasChanges(true);
                }}
              />
            </div>
          </div>
          <p id="low-balance-help" className={`m-0 text-xs ${threshold === 'invalid' ? 'text-ps-err' : 'text-ps-subtle'}`}>
            {threshold === 'invalid'
              ? 'Enter an amount in rupees, or leave it empty.'
              : 'Leave empty to use the platform defaults. Turn the alert on or off under Events.'}
          </p>
        </section>
      )}

      <section className="ps-card px-5 sm:px-6 pt-3 pb-2" aria-labelledby="ev-heading">
        <div className={`${rowCls} !border-t-0`}>
          <h2 id="ev-heading" className="m-0 text-lg font-medium text-ps-text">
            Events
          </h2>
          {supportsChannels ? (
            <>
              <span className={hdCls}>Email</span>
              <span className={hdCls}>In-app</span>
            </>
          ) : (
            <span className={hdCls}>Notify</span>
          )}
        </div>
        {events.map((setting) => {
          const ch = form.channels[setting.id];
          const text = (
            <div className="min-w-0">
              <div className="font-medium text-ps-text">{setting.label}</div>
              <div className="text-ps-subtle text-[13px]">{setting.description}</div>
            </div>
          );
          return supportsChannels ? (
            <div key={setting.id} className={rowCls}>
              {text}
              <input
                type="checkbox"
                className={checkboxCls}
                checked={ch.email}
                onChange={() => toggle(setting.id, 'email')}
                aria-label={`${setting.label} by email`}
              />
              <input
                type="checkbox"
                className={checkboxCls}
                checked={ch.in_app}
                onChange={() => toggle(setting.id, 'in_app')}
                aria-label={`${setting.label} in app`}
              />
            </div>
          ) : (
            <label key={setting.id} className={rowCls}>
              {text}
              <input
                type="checkbox"
                className={checkboxCls}
                checked={ch.email}
                onChange={() => toggle(setting.id, 'email')}
                aria-label={setting.label}
              />
            </label>
          );
        })}
      </section>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateMutation.isPending || !hasChanges}>
          {updateMutation.isPending ? 'Saving…' : 'Save preferences'}
        </Button>
      </div>
    </>
  );
};

export default NotificationSettings;
